/** The Customize strip on a card (Home spec §4): a handle, the three widths,
 * Move up / down (the keyboard's way to reorder), Remove. Every name says
 * which widget, so a screen reader hears "Remove Fleet", never "Remove". */
import { ArrowDown, ArrowUp, GripVertical, X } from 'lucide-react'
import type { HTMLAttributes, ReactElement } from 'react'
import type { HomeWidgetEntry, HomeWidth } from '@/components/custom/homeWidgetTypes'
import { clampWidth, WIDTHS } from './homeLayout'
import { WIDTH_WORD } from './homeWords'
import { Tip } from '@/components/ui'

export interface WidgetToolsProps {
  entry: HomeWidgetEntry
  width: HomeWidth
  /** False at the end of the card's lane (or of the list below the list frame). */
  canMoveUp: boolean
  canMoveDown: boolean
  onMove: (direction: -1 | 1) => void
  onWidth: (width: HomeWidth) => void
  onRemove: () => void
  /** dnd-kit's listeners and attributes for the grip. */
  handle: HTMLAttributes<HTMLButtonElement>
}

const tipWhen = (enabled: boolean, text: string, button: ReactElement) => (enabled ? <Tip text={text}>{button}</Tip> : button)

export function WidgetTools({ entry, width, canMoveUp, canMoveDown, onMove, onWidth, onRemove, handle }: WidgetToolsProps) {
  return (
    <div className="-mx-1.5 -mt-1.5 flex flex-wrap items-center gap-1.5 rounded-md border border-dashed border-border bg-muted px-2 py-1.5 text-xs">
      {/* SA-1034 PR 5 (spec §3.5): a bordered grip, so it reads as a handle at rest. The dnd-kit
          `handle` props are the child's and win over the trigger's in Radix's Slot merge. */}
      <Tip text="Drag to reorder">
        <button type="button" className="cursor-grab rounded border border-border bg-background px-0.5 py-0.5 text-muted-foreground active:cursor-grabbing" aria-label={`Drag ${entry.title}`} {...handle}><GripVertical className="size-4" /></button>
      </Tip>
      <span role="group" aria-label={`Width of ${entry.title}`} className="inline-flex overflow-hidden rounded-md border border-border bg-background">
        {WIDTHS.filter((w) => clampWidth(entry, w) === w).map((w) => (
          <button key={w} type="button" aria-pressed={width === w} onClick={() => onWidth(w)}
            className={`px-2 py-0.5 text-[11.5px] ${width === w ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'}`}>{WIDTH_WORD[w]}</button>
        ))}
      </span>
      {/* SA-1418 (B3): a disabled button takes no pointer or focus, so it carries no tip; its name stays. */}
      {tipWhen(canMoveUp, `Move ${entry.title} up`, <button type="button" aria-label={`Move ${entry.title} up`} disabled={!canMoveUp} onClick={() => onMove(-1)} className="p-0.5 text-muted-foreground disabled:opacity-35"><ArrowUp className="size-4" /></button>)}
      {tipWhen(canMoveDown, `Move ${entry.title} down`, <button type="button" aria-label={`Move ${entry.title} down`} disabled={!canMoveDown} onClick={() => onMove(1)} className="p-0.5 text-muted-foreground disabled:opacity-35"><ArrowDown className="size-4" /></button>)}
      <Tip text={`Remove ${entry.title}`}><button type="button" aria-label={`Remove ${entry.title}`} onClick={onRemove} className="ml-auto p-0.5 text-muted-foreground"><X className="size-4" /></button></Tip>
    </div>
  )
}
